import { useMemo } from 'react';

import { Drawer, Empty } from 'antd';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumn } from '@src/components/Table';
import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import IntlMessages from '@src/util/IntlMessages';

import {
  hasAttendanceJourneyStarted,
  partitionJourneyMembersByMarking,
} from '../../utils/attendanceMetrics';

import styles from './MarkedJourneyUsersDrawer.module.less';

export type MarkedJourneyUsersDrawerVariant = 'roster' | 'fix';

export interface MarkedJourneyUsersDrawerProps {
  open: boolean;
  users: AttendanceDashboardItem[];
  onClose: () => void;
  onViewUserDetail?: (user: AttendanceDashboardItem) => void;
  onMarkUser?: (user: AttendanceDashboardItem) => void;
  onReplaceUser?: (user: AttendanceDashboardItem) => void;
  /** `roster` shows marked + unmarked; `fix` shows only unmarked with mark/replace. */
  variant?: MarkedJourneyUsersDrawerVariant;
}

export const MarkedJourneyUsersDrawer = ({
  open,
  users,
  onClose,
  onViewUserDetail,
  onMarkUser,
  onReplaceUser,
  variant = 'roster',
}: MarkedJourneyUsersDrawerProps) => {
  const intl = useIntl();
  const { countryRulesForm } = useRulesByCountry();
  const identifierLabel = intl.formatMessage({
    id: countryRulesForm?.identifier?.label ?? 'country.chile.identifier',
  });
  const isFixVariant = variant === 'fix';

  const { marked, unmarked } = useMemo(
    () => partitionJourneyMembersByMarking(users),
    [users],
  );

  const columns = useMemo<ResponsiveTableColumn<AttendanceDashboardItem>[]>(
    () => [
      {
        key: 'user',
        title: intl.formatMessage({ id: 'general.user' }),
        dataIndex: 'userDisplayName',
        render: (record: AttendanceDashboardItem) => record.userDisplayName,
      },
      {
        key: 'idCard',
        title: identifierLabel,
        dataIndex: 'userIdCard',
        align: 'center',
        render: (record: AttendanceDashboardItem) =>
          record.userIdCard
            ? countryRulesForm?.identifier?.formatIdentifier(record.userIdCard) ??
              record.userIdCard
            : '—',
      },
      {
        key: 'email',
        title: intl.formatMessage({ id: 'users.select.table.email' }),
        dataIndex: 'userEmail',
        render: (record: AttendanceDashboardItem) => record.userEmail ?? '—',
      },
      {
        key: 'action',
        title: '',
        dataIndex: 'id',
        align: 'center',
        columnType: 'actions',
        actionsLayout: 'center',
        width: isFixVariant ? 120 : 56,
        render: (record: AttendanceDashboardItem) => {
          const hasMarked = hasAttendanceJourneyStarted(record);
          if (isFixVariant || !hasMarked) {
            return (
              <ActionButton.Container
                maxVisibleButtons={2}
                buttons={[
                  <ActionButton
                    key="mark"
                    icon="how_to_reg"
                    allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
                    tooltipText={intl.formatMessage({
                      id: 'workJourney.realTime.markAttendance.markUser',
                    })}
                    tooltipPosition="top"
                    onClick={() => onMarkUser?.(record)}
                  />,
                  <ActionButton
                    key="replace"
                    icon="swap_horiz"
                    allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
                    tooltipText={intl.formatMessage({
                      id: 'workJourney.realTime.action.replace',
                    })}
                    tooltipPosition="top"
                    color="danger"
                    onClick={() => onReplaceUser?.(record)}
                  />,
                ]}
              />
            );
          }

          return (
            <ActionButton
              icon="visibility"
              tooltipText={intl.formatMessage({ id: 'workJourney.realTime.action.view' })}
              tooltipPosition="top"
              onClick={() => onViewUserDetail?.(record)}
            />
          );
        },
      },
    ],
    [
      countryRulesForm,
      identifierLabel,
      intl,
      isFixVariant,
      onMarkUser,
      onReplaceUser,
      onViewUserDetail,
    ],
  );

  const renderSection = (
    sectionKey: 'marked' | 'unmarked',
    titleId: string,
    dataSource: AttendanceDashboardItem[],
    emptyId: string,
  ) => (
    <section key={sectionKey} className={styles.section}>
      <div className={styles.sectionHeader}>
        <Title.SubTitle value={<IntlMessages id={titleId} />} />
        <span className={styles.sectionCount}>{dataSource.length}</span>
      </div>
      {dataSource.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={intl.formatMessage({ id: emptyId })}
          className={styles.emptyState}
        />
      ) : (
        <ResponsiveTable<AttendanceDashboardItem>
          type="light"
          dataSource={dataSource}
          columns={columns}
        />
      )}
    </section>
  );

  const titleId = isFixVariant
    ? 'workJourney.realTime.fixUnmarked.title'
    : 'workJourney.realTime.markedUsers.title';

  return (
    <Drawer
      destroyOnClose
      open={open}
      onClose={onClose}
      width="40vw"
      title={null}
      closable={false}
      footer={null}
      className={styles.drawerRoot}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={onClose}
            title={<Title.Header value={intl.formatMessage({ id: titleId })} />}
          />
        </BoxContainer>
        <BoxContainer content>
          {isFixVariant
            ? renderSection(
                'unmarked',
                'workJourney.realTime.markedUsers.unmarkedSection',
                unmarked,
                'workJourney.realTime.markedUsers.unmarkedEmpty',
              )
            : (
              <>
                {renderSection(
                  'marked',
                  'workJourney.realTime.markedUsers.markedSection',
                  marked,
                  'workJourney.realTime.markedUsers.markedEmpty',
                )}
                {renderSection(
                  'unmarked',
                  'workJourney.realTime.markedUsers.unmarkedSection',
                  unmarked,
                  'workJourney.realTime.markedUsers.unmarkedEmpty',
                )}
              </>
            )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
